import type { BackgroundCheckDispositionStatus } from "./mutations";

/**
 * Staff select is intentionally limited to three writable choices.
 * GraphQL still accepts five statuses; `toWritableStatus` maps legacy/current
 * record values into these options so Edit prefills without expanding the UI.
 */
export const WRITABLE_STATUS_OPTIONS: ReadonlyArray<{
  value: BackgroundCheckDispositionStatus;
  label: string;
}> = [
  { value: "APPROVED", label: "Approved" },
  { value: "DENIED", label: "Denied" },
  { value: "IN_REVIEW", label: "In Review" }
] as const;

export type BackgroundCheckDispositionFormValues = {
  status: BackgroundCheckDispositionStatus | "";
  screenedOn: string;
  note: string;
};

/**
 * Prefill mapping into the three-option select:
 * - DOCUMENTATION_NEEDED / NEED_DOCUMENTATION / UNDER_REVIEW → IN_REVIEW
 * - IN_PROGRESS → blank (no matching select option)
 * - EXPIRED → APPROVED
 * Saving without changing Status therefore writes the mapped select value.
 */
export function toWritableStatus(status: string | null | undefined): BackgroundCheckDispositionStatus | "" {
  if (!status) return "";
  if (status === "EXPIRED") return "APPROVED";
  if (status === "NEED_DOCUMENTATION" || status === "DOCUMENTATION_NEEDED" || status === "UNDER_REVIEW") {
    return "IN_REVIEW";
  }
  if (status === "IN_PROGRESS") return "";
  const match = WRITABLE_STATUS_OPTIONS.find((option) => option.value === status);
  return match?.value ?? "";
}

export function isExpiredDisplayStatus(status: string | null | undefined): boolean {
  return status === "EXPIRED";
}

export function toScreenedOnPrefill(date: string | null | undefined): string {
  if (!date) return "";
  const match = date.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? "";
}

export function addOneYearIsoDate(screenedOn: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(screenedOn)) return null;
  const [year, month, day] = screenedOn.split("-").map(Number);
  const next = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(next.getTime())) return null;
  next.setUTCFullYear(next.getUTCFullYear() + 1);
  return next.toISOString().slice(0, 10);
}

export function isFutureIsoDate(date: string, today = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const todayIso = today.toISOString().slice(0, 10);
  return date > todayIso;
}

export function requiresDispositionConfirm(status: BackgroundCheckDispositionStatus | ""): boolean {
  return status === "APPROVED" || status === "DENIED";
}
